import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { BehaviorSubject, combineLatest, Observable, of, shareReplay } from 'rxjs';
import { combineLatestWith, distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';

import { Store } from '@ngxs/store';

import { ProfileIdea } from '@smarthead-api/models/profile-idea';

import { LanguageService } from '@shared/modules/language/services/language.service';

import { DeviceService } from '../../../services/device.service';
import { LOCK_SCREEN, RouterLinkCreatorService } from '../../../services/router-link-creator.service';
import { AppState } from '../../../state/app.state';
import { isLinkFromOnboarding } from '../../../utils/onboarding-utils';
import { MenuTabs } from '../components/main-nav/main-nav.component';
import { MainNavInbox, MainNavSingleItemData } from '../model/main-nav.model';

@Injectable()
export class MainNavService {
  readonly #languageService = inject(LanguageService);
  loggedUser$ = this.store.select(AppState.getUserData);
  isSuperAdmin$ = this.store.select(AppState.isUserSHAdmin);
  hasUserAllDataLoaded$ = this.store.select(AppState.hasUserAllDataLoaded);
  profileWhereUserAdmin$ = this.store.select(AppState.getProfilesWhereUserIsAdmin);
  profileSendIdeas$ = this.store.select(AppState.getProfileSendIdeas);

  unSeenProfileSendIdeas$: Observable<ProfileIdea[]> = new BehaviorSubject([]);
  hasInbox$: Observable<boolean>;
  hasProfile$: Observable<boolean>;

  inbox$: Observable<MainNavInbox | null>;
  requests$: Observable<MainNavSingleItemData | null>;

  activeUrl$: Observable<MenuTabs | null>;
  fullUrl$: Observable<string>;

  device$ = this.deviceService.type$;

  isPlatform$: Observable<boolean>;
  isFullWidthHeader$: Observable<boolean>;

  constructor(
    protected store: Store,
    private routerLinkCreatorService: RouterLinkCreatorService,
    public router: Router,
    private deviceService: DeviceService
  ) {
    this.device$.subscribe();

    this.hasInbox$ = combineLatest([
      this.loggedUser$.pipe(filter(loggedUser => !!loggedUser)),
      this.hasUserAllDataLoaded$,
      this.profileWhereUserAdmin$.pipe(filter(profiles => !!profiles?.length))
    ]).pipe(map(composition => composition.every(() => true)));

    this.hasProfile$ = this.profileWhereUserAdmin$.pipe(
      map(profiles => !!profiles?.length),
      startWith(false)
    );

    this.inbox$ = this.hasInbox$.pipe(
      switchMap(hasInbox => {
        if (!hasInbox) {
          return of(null);
        }

        return this.loggedUser$.pipe(
          // filter((loggedUser) => !!loggedUser),
          switchMap(loggedUser => {
            if (!loggedUser) {
              return of(null);
            }

            return this.unSeenProfileSendIdeas$.pipe(
              map(unSeenProfileSendIdeas => ({
                link: this.routerLinkCreatorService.createProfileIdeasLink(loggedUser.id, loggedUser.text),
                notificationCount: unSeenProfileSendIdeas.length
              }))
            );
          })
        );
      })
    );

    // createTaskManagementLink uses createDomainLangPrefix which takes language value from this.languageService.current$
    // which takes value from cookies, and all this has a pretty big delay compared to language change in transloco
    // So we wait directly for the "source of truth", even though I don't like it at all. But it should at least be robust.
    this.requests$ = this.#languageService.current$.pipe(
      distinctUntilChanged(),
      combineLatestWith(this.loggedUser$),
      filter(([_lang, loggedUser]) => !!loggedUser),
      map(([_lang, loggedUser]) => {
        if (!loggedUser) {
          return null;
        }
        return {
          link: this.routerLinkCreatorService.createTaskManagementLink(loggedUser.id, loggedUser.text),
          title: 'header.requests'
        } as MainNavSingleItemData;
      })
    );

    if (this.profileSendIdeas$) {
      this.unSeenProfileSendIdeas$ = this.profileSendIdeas$.pipe(
        startWith([]),
        filter(ideas => !!ideas),
        map(ideas => (ideas ? ideas.filter(i => !i.seen) : []))
      );
    }

    this.fullUrl$ = this.router.events.pipe(
      startWith(this.router.url),
      filter(event => event instanceof NavigationEnd || typeof event === 'string'),
      map(event => {
        if (typeof event === 'string') {
          return event;
        } else if (event instanceof NavigationEnd) {
          return event.url;
        } else {
          return '';
        }
      }),
      filter(url => !!url),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.activeUrl$ = this.fullUrl$.pipe(map(url => this.parseUrl(url)));

    this.isPlatform$ = this.activeUrl$.pipe(
      map(url => this.isPlatform(url)),
      distinctUntilChanged()
    );

    this.isFullWidthHeader$ = this.fullUrl$.pipe(
      map(url => this.isFullWidthHeader(url)),
      distinctUntilChanged()
    );
  }

  checkIfCanShowFooter(): Observable<boolean> {
    return this.router.events.pipe(
      startWith<any, any>(this.router.url),
      filter((eventOrUrl: NavigationEnd | string) => eventOrUrl instanceof NavigationEnd || typeof eventOrUrl === 'string'),
      map((eventOrUrl: NavigationEnd | string) => (eventOrUrl instanceof NavigationEnd ? eventOrUrl.url : eventOrUrl)),
      map((url: string) => {
        if (!url) {
          return false;
        }

        if (isLinkFromOnboarding(url) || url.indexOf(LOCK_SCREEN) > 0) {
          return false;
        }

        return !((url.includes('profile/') && !url.includes('admin')) || url.includes('newsfeed') || url.includes('task-management'));
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private parseUrl(url: string): MenuTabs | null {
    if (!url) {
      return 'newsfeed';
    }

    const urlArr = url.split('/');
    if (urlArr.length > 0) {
      if (urlArr.length < 2) {
        return 'newsfeed';
      }
      const slug = urlArr.length >= 2 ? urlArr[2] : 'newsfeed';
      const categorySlug = urlArr.length >= 3 ? urlArr[3] : '';
      const ideaSlug = urlArr.length >= 5 ? urlArr[5] : '';
      if (slug === '') {
        return 'newsfeed';
      }

      if ([
          'newsfeed',
          'business',
          'sustainability-profile',
          'data-analytics',
          'profile',
          'activity',
          'our-purpose',
          'about-us',
          'advisory-board',
          'contact',
          'purchase',
          'search'
        ].includes(slug)) {
        return slug as MenuTabs;
      }

      if (slug === 'ranks') {
        return categorySlug === 'category' ? 'categories' : 'ranks';
      }

      if (slug === 'pricing') {
        return 'purchase';
      }

      if (slug === 'user') {
        return ideaSlug?.startsWith('profile-ideas') ? 'inbox' : 'user';
      }

      if (
        slug === 'media' ||
        (urlArr.length >= 2 && urlArr[urlArr.length - 2] === 'media') || // media list page
        (urlArr.length >= 3 && urlArr[urlArr.length - 3] === 'media') // media post (detail) page
      ) {
        return 'media';
      }

      return null;
    }
    return null;
  }

  private isPlatform(tab: MenuTabs | null): boolean {
    return [
      'user',
      'inbox',
      'profile',
      'activity',
      'newsfeed',
      'ranks',
      'categories',
      'search'
    ].includes(tab as string);
  }

  private isFullWidthHeader(url: string | null): boolean {
    if (url?.includes('esg-management') && url?.includes('edit')) {
      return true;
    }

    if (url?.endsWith('admin') || url?.endsWith('admin/update') || url?.endsWith('activity/create')) {
      return true;
    }

    return false;
  }
}
